import type { IMessageHeader } from "../hooks/reactQueryHooks/useProxy";
import type { updateMessageProps } from "../pages/playground/types";
import { Stream } from "./StreamParser";

interface Error {
  error: {
    message: string;
    type: string;
  };
  status: number;
  name: string;
  message: string;
}

export interface onCompleteProps {
  completionTokens: number;
  latency: number;
}

export interface CheckRestrictionsProps {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: {
    content_filter_results: {
      [key: string]: {
        filtered: boolean;
        severity: string;
      };
    };
    delta: {
      content: string;
      tool_calls?: {
        function: {
          name: string;
          arguments: string;
        };
        id: string;
      }[];
    };
    finish_reason: string;
    index: number;
    logprobs: null;
    message: {
      content: string;
      role: string;
    };
  }[];
  usage: {
    completion_tokens: number;
    prompt_tokens: number;
    total_tokens: number;
    latency_time: number;
  } | null;
}

export const checkRestrictions = (item: CheckRestrictionsProps) => {
  const filters = item?.choices?.[0]?.content_filter_results;
  if (filters) {
    const filersKeys = Object.keys(filters);
    if (filersKeys.length > 0) {
      return (
        filersKeys
          .map(key => {
            const filter = filters[key];
            if (filter?.filtered) {
              return key;
            }
            return "";
          })
          .find(item => item) || ""
      );
    }
  }
  return "";
};

export interface ErrorResponse {
  type: string;
  message: string;
  headers?: IMessageHeader;
  id?: number;
}

export interface ReadStreamProps {
  response: Response;
  initialTokensCount?: number;
  onUpdate: (message: updateMessageProps) => void;
  onComplete: () => void;
  onError: (error: ErrorResponse) => void;
  cacheType: string;
}

export const readStream = async ({
  response,
  onUpdate,
  onComplete,
  onError,
  cacheType,
}: ReadStreamProps) => {
  const id = Date.now();

  const headers: IMessageHeader = {
    hitType: response.headers.get("Cogcache-Hit-Type"),
    xCache: response.headers.get("X-Cache"),
    similarityScore: response.headers.get("Cogcache-Similarity-Match-Score"),
    cacheType: cacheType,
  };

  const controller = new AbortController();

  const stream = new Stream<CheckRestrictionsProps>(response, controller);

  try {
    for await (const item of stream) {
      ((item: CheckRestrictionsProps) => {
        const restriction = checkRestrictions(item);
        if (restriction) {
          onError({
            type: "restriction",
            message: `The system stopped generating a response since it contained ${restriction}.`,
            headers,
            id,
          });
          controller.abort();
          throw new Error(restriction);
        }
      })(item);

      if (item?.usage) {
        headers.latency = item.usage.latency_time;
        headers.tokensPerSecond =
          Math.round(item.usage.completion_tokens / item.usage.latency_time) ||
          null;
      }

      onUpdate({
        message: item,
        headers,
        id,
      });
    }
    if (response.status !== 200) {
      const error = JSON.parse(stream.decoder.chunks[0]) as Error;
      onError({
        type: "abort",
        message: error?.error.message || "Error",
        id,
      });
    }
    onComplete();
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      onError({
        type: "abort",
        message: "Stream was aborted",
      });
    }
  }
};
